import React, { useEffect } from 'react';
import { Box, Grid, Typography, Avatar, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import MiniWebSessionCard from '../../components/MiniWebSessionCard/MiniWebSessionCard';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchCoachAvailabilitiesForMiniweb,
  getCoachPublicDetailsRequest,
} from '../../redux/miniweb/miniwebSlice';
import { getInitials } from '../../utils/userInputUtils';
import { COACH_PROFILE_SET_BOOKING_DATE_ROUTE, NOT_FOUND_ROUTE } from '../../utils/routes';
import WarningBanner from './WarningBanner/WarningBanner';
import { formatAddress, formatPhoneNumber } from '../../utils/utils';
import MiniWebSiteSkeleton from './MiniWebSiteSkeleton';
import MiniWebSiteHeader from './MiniWebSiteHeader/MiniWebSiteHeader';
import MiniWebSiteFooter from './MiniWebSiteFooter/MiniWebSiteFooter';

const getFirstName = (fullName: string) => {
  const nameArray = fullName.split(' ');
  return nameArray[0];
};

export const getGridSx = (servicePackagesLength: number) => {
  if (servicePackagesLength > 1) {
    return {
      width: '100%',
      maxWidth: '766px',
      display: 'grid',
      justifyContent: 'center',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '8px',
      '@media (max-width: 1024px)': {
        gridTemplateColumns: 'repeat(2, 1fr)',
      },
      '@media (max-width: 600px)': {
        gridTemplateColumns: '1fr',
      },
    };
  }
  return {
    width: '100%',
    maxWidth: '766px',
    display: 'grid',
    justifyContent: 'center',
    gap: '8px',
  };
};

const MiniWebSite = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { username } = useParams();

  const {
    firstName,
    lastName,
    profilePic,
    coachTypes,
    servicePackages,
    officeAddress,
    officePhone,
    calendar,
    videoCallPlatform,
    isProvideOnlineConsultations,
    isProvideInPersonConsultations,
  } = useAppSelector((state) => state.miniweb.coachPublicDetails);
  const { isUsernameInvalid, getPublicDetailsLoading } = useAppSelector((state) => state.miniweb);
  const coachName = `${firstName} ${lastName}`;

  const shouldDisplayPhone = officePhone !== null && officePhone !== undefined;
  const shouldDisplayAddress = officeAddress !== null && officeAddress !== undefined;

  const noPhoneAndAddress = !shouldDisplayPhone && !shouldDisplayAddress;
  const getProfilePicUrl = () => {
    if (!profilePic) return getInitials(firstName, lastName);
    if (profilePic && (profilePic as string).startsWith('https://')) return profilePic;
    return new URL(profilePic, process.env.REACT_APP_MEDIA_FILES_BASE_URL).href;
  };
  const { coachAvailabilities } = useAppSelector((state) => state.miniweb);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (username) {
      dispatch(fetchCoachAvailabilitiesForMiniweb({ username }));
    }
  }, [dispatch, username]);

  const noAvailableSlots = !coachAvailabilities?.length;

  useEffect(() => {
    if (username) {
      dispatch(getCoachPublicDetailsRequest({ username }));
    } else {
      navigate(NOT_FOUND_ROUTE);
    }
  }, [username, dispatch]);

  useEffect(() => {
    if (isUsernameInvalid) {
      navigate(NOT_FOUND_ROUTE);
    }
  }, [isUsernameInvalid, navigate]);

  const tags = coachTypes ? coachTypes.map((type) => type.coachType) : [];

  const handleNavigateToBookingDates = (servicePackage) => {
    if (username) {
      navigate(COACH_PROFILE_SET_BOOKING_DATE_ROUTE.replace(':username', username), {
        state: {
          username,
          servicePackage,
        },
      });
    }
  };

  return (
    <Grid
      container
      display="flex"
      sx={{
        background: 'linear-gradient(180deg, #FFFFFF 0%, #FFEEE7 100%)',
        minHeight: '100vh',
        padding: '16px',
        gap: '36px',
      }}
      alignItems="center"
      justifyContent="center"
    >
      <MiniWebSiteHeader />
      {getPublicDetailsLoading ? (
        <MiniWebSiteSkeleton />
      ) : (
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
          mt={5}
          mb={5}
          sx={{ gap: '32px', width: '100%' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '24px',
            }}
          >
            <Avatar
              alt={coachName}
              src={getProfilePicUrl()}
              sx={{
                width: 130,
                height: 130,
                border: '5px solid #FFDDC8',
                boxShadow: '0px 4px 40px 0px #FFDDC840',
                fontSize: '36px',
                bgcolor: profilePic ? 'transparent' : '#FFDDC8',
              }}
            >
              {getProfilePicUrl()}
            </Avatar>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Typography variant="title1Bold" color="primary">
                {coachName}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: '10px',
                  maxWidth: '300px',
                }}
              >
                {tags.map((tag) => (
                  <Box
                    key={tag}
                    sx={{
                      padding: '4px 12px',
                      borderRadius: '100px',
                      background: '#FFDDC8',
                    }}
                  >
                    <Typography variant="bodyBold" fontSize={12} color="primary">
                      {t(`coachTypes.${tag}`)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
          {!noPhoneAndAddress && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {shouldDisplayPhone && officePhone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <img src="/assets/icons/phone.svg" alt="phone" height={16} width={16} />
                  <Typography
                    variant="meta"
                    color="primary"
                    sx={{
                      direction: 'ltr',
                      unicodeBidi: 'plaintext',
                    }}
                  >
                    {formatPhoneNumber(officePhone)}
                  </Typography>
                </Box>
              )}
              {shouldDisplayAddress && officeAddress && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                  <img src="/assets/icons/address.svg" alt="location" height={16} width={16} />
                  <Typography
                    variant="meta"
                    color="primary"
                    sx={{
                      whiteSpace: 'pre-line',
                      wordBreak: 'break-word',
                      textAlign: 'center',
                    }}
                  >
                    {formatAddress(officeAddress)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '16px',
              width: '100%',
            }}
          >
            <Typography variant="bodyBold" color="primary">
              {t(`miniWebSite.subhead`)} {getFirstName(coachName)}
            </Typography>
            <Box
              sx={{
                maxWidth: isMobile ? '100%' : '70%',
              }}
            >
              {firstName && !getPublicDetailsLoading && !isUsernameInvalid && (
                <WarningBanner
                  username={firstName}
                  noAvailableSlots={noAvailableSlots}
                  isProvideOnlineConsultations={isProvideOnlineConsultations}
                />
              )}
            </Box>

            <Box sx={getGridSx(servicePackages.length)}>
              {servicePackages?.map((session) => (
                <MiniWebSessionCard
                  key={session.id}
                  title={session.title}
                  bookingType={session.bookingType}
                  price={session.sessionPrice}
                  description={session.sessionDescription}
                  duration={session.sessionDuration}
                  onBookNowClick={() => handleNavigateToBookingDates(session)}
                  noCalendar={calendar === null}
                  noVideoCallPlatform={videoCallPlatform === null}
                  username={firstName}
                  noAvailableSlots={noAvailableSlots}
                  isProvideOnlineConsultations={isProvideOnlineConsultations}
                  isProvideInPersonConsultations={isProvideInPersonConsultations}
                />
              ))}
            </Box>
          </Box>
        </Grid>
      )}
      <MiniWebSiteFooter />
    </Grid>
  );
};

export default MiniWebSite;
